import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { CalculatorResultCard, CalculatorActionsBar } from "@/components/public/calculators"
import { encodeCalculatorParams, decodeCalculatorParams } from "@/lib/calculator-url-params"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"
import type { SeoMeta } from "@/types"
import {
  STAMP_DUTY_DEFAULTS,
  BUYER_TYPE_OPTIONS,
  calculateStampDuty,
  formatCurrency,
  formatPercent,
} from "@/data/tools/stamp-duty-rates"
import type { BuyerType } from "@/data/tools/stamp-duty-rates"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "What is stamp duty (SDLT)?",
    a: "Stamp Duty Land Tax (SDLT) is a tax paid when you buy residential property in England or Northern Ireland above a certain price. Scotland has LBTT (Land and Buildings Transaction Tax) and Wales has LTT (Land Transaction Tax). This calculator covers SDLT only.",
  },
  {
    q: "Do first-time buyers pay stamp duty?",
    a: "First-time buyers pay no stamp duty on properties up to \u00A3425,000. Between \u00A3425,000 and \u00A3625,000, you pay 5% on the portion above \u00A3425,000 only. If the property costs more than \u00A3625,000, first-time buyer relief doesn't apply and you pay standard rates.",
  },
  {
    q: "What is the additional property surcharge?",
    a: "If you're buying a second home or buy-to-let investment property, you pay a 5% surcharge on top of the standard SDLT rates. This applies to the entire purchase price, starting from the first pound. The surcharge increased from 3% to 5% in October 2024.",
  },
  {
    q: "When do I pay stamp duty?",
    a: "You must file an SDLT return and pay the tax within 14 days of completion. Your solicitor or conveyancer normally handles this for you as part of the purchase process.",
  },
  {
    q: "Does stamp duty apply in Scotland and Wales?",
    a: "No. Scotland uses Land and Buildings Transaction Tax (LBTT) and Wales uses Land Transaction Tax (LTT). Both have different rates and thresholds. This calculator covers England and Northern Ireland only.",
  },
  {
    q: "What are the current SDLT rates for 2026?",
    a: "For standard purchases in England and Northern Ireland: 0% on the first \u00A3250,000, 5% on the portion from \u00A3250,001 to \u00A3925,000, 10% from \u00A3925,001 to \u00A31.5 million, and 12% on anything above \u00A31.5 million. First-time buyers and additional property purchases have different thresholds.",
  },
  {
    q: "Can I add stamp duty to my mortgage?",
    a: "You cannot add stamp duty directly to your mortgage. SDLT must be paid separately at completion. However, some buyers factor stamp duty into their borrowing by taking a slightly larger mortgage on the property itself, though this increases your loan-to-value ratio and may affect your interest rate.",
  },
  {
    q: "Is stamp duty different for leasehold properties?",
    a: "The SDLT rates for leasehold purchases are the same as for freehold. However, if you're buying a new leasehold property, there may be additional SDLT on the net present value of the ground rent if it exceeds certain thresholds. Your solicitor will advise on this.",
  },
  {
    q: "Do I pay stamp duty on a property I inherit?",
    a: "No. Inherited property is not subject to stamp duty because there is no purchase transaction. However, if you already own a property and inherit another, the inherited property may affect whether you pay the additional property surcharge on future purchases.",
  },
  {
    q: "How does stamp duty work for shared ownership?",
    a: "With shared ownership, you can choose to pay SDLT on the share you're buying or on the full market value. If you pay on just your share, you'll owe additional SDLT when you staircase (buy more shares). Most buyers opt to pay on the full value upfront if the total SDLT would be lower.",
  },
]

/* ------------------------------------------------------------------ */
/* Slider component                                                    */
/* ------------------------------------------------------------------ */

function PriceSlider({
  value,
  onChange,
}: {
  value: number
  onChange: (v: number) => void
}) {
  const min = 50_000
  const max = 2_000_000
  const step = 5_000
  const percent = ((value - min) / (max - min)) * 100

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-charcoal">
          Property price
        </label>
        <span className="font-display text-lg font-bold text-charcoal">
          {formatCurrency(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-stone accent-red"
        style={{
          background: `linear-gradient(to right, #C41E2A ${percent}%, #E8E4DF ${percent}%)`,
        }}
      />
      <div className="mt-1 flex justify-between text-[10px] text-warm-grey">
        <span>{formatCurrency(min)}</span>
        <span>{formatCurrency(max)}</span>
      </div>

      {/* Direct input */}
      <div className="mt-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-warm-grey">
            £
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={value.toLocaleString("en-GB")}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "")
              const num = parseInt(raw, 10)
              if (!isNaN(num) && num >= 0 && num <= 50_000_000) {
                onChange(num)
              }
            }}
            className="w-full rounded-lg border border-stone bg-white py-2.5 pl-7 pr-3 text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-1 focus:ring-red/20"
          />
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Band breakdown table                                                */
/* ------------------------------------------------------------------ */

function BandBreakdown({
  bands,
}: {
  bands: { from: number; to: number; rate: number; duty: number }[]
}) {
  if (bands.length === 0) return null

  return (
    <div className="rounded-xl border border-stone bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
        Tax band breakdown
      </p>
      <div className="mt-3 space-y-2">
        {bands.map((band, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-slate">
              {formatCurrency(band.from)} &ndash; {formatCurrency(band.to)}
            </span>
            <div className="flex items-center gap-4">
              <span className="text-xs text-warm-grey">{band.rate}%</span>
              <span className="font-medium text-charcoal">
                {formatCurrency(band.duty)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function StampDutyCalculatorPage({
  meta,
}: {
  meta: SeoMeta
}) {
  const initialParams = useMemo(() => {
    if (typeof window === "undefined") return STAMP_DUTY_DEFAULTS
    const sp = new URLSearchParams(window.location.search)
    return decodeCalculatorParams(
      { propertyPrice: STAMP_DUTY_DEFAULTS.propertyPrice, buyerType: STAMP_DUTY_DEFAULTS.buyerType as string },
      sp,
    )
  }, [])

  const [price, setPrice] = useState<number>(initialParams.propertyPrice as number)
  const [buyerType, setBuyerType] = useState<BuyerType>(
    initialParams.buyerType as BuyerType,
  )

  useEffect(() => {
    const params = encodeCalculatorParams({ propertyPrice: price, buyerType })
    window.history.replaceState(null, "", `?${params}`)
  }, [price, buyerType])

  const result = calculateStampDuty(price, buyerType)

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?${encodeCalculatorParams({ propertyPrice: price, buyerType })}`
      : ""
  const shareTitle = `Stamp duty on a ${formatCurrency(price)} property: ${formatCurrency(result.totalDuty)} (${formatPercent(result.effectiveRate)} effective rate)`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Stamp Duty Calculation",
      subtitle: "SDLT rates for England & Northern Ireland, 2026",
      sections: [
        {
          title: "Results",
          rows: [
            { label: "Total stamp duty", value: formatCurrency(result.totalDuty), sub: `Effective rate: ${formatPercent(result.effectiveRate)}`, accent: true },
            { label: "Property price", value: formatCurrency(price), sub: BUYER_TYPE_OPTIONS.find((o) => o.value === buyerType)?.label },
            { label: "Total cost", value: formatCurrency(price + result.totalDuty), sub: "Purchase price + stamp duty" },
          ],
        },
        {
          title: "Tax Band Breakdown",
          rows: result.bands.map((band) => ({
            label: `${formatCurrency(band.from)} to ${formatCurrency(band.to)}`,
            value: formatCurrency(band.duty),
            sub: `${band.rate}% rate`,
          })),
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Structured Data — WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Stamp Duty Calculator 2026",
            description: meta.description,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP",
            },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        {/* Structured Data — FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Stamp Duty Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Stamp duty calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Calculate how much stamp duty (SDLT) you&rsquo;ll pay on a
            residential property purchase in England or Northern Ireland.
            Updated for 2026 rates.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-8">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Property details<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Enter the purchase price and select your buyer type.
                </p>
              </div>

              <PriceSlider value={price} onChange={setPrice} />

              {/* Buyer type */}
              <div>
                <label className="text-sm font-medium text-charcoal">
                  Buyer type
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {BUYER_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setBuyerType(option.value)}
                      className={`rounded-lg border px-3 py-3 text-left transition-all ${
                        buyerType === option.value
                          ? "border-red bg-red/5 ring-1 ring-red/20"
                          : "border-stone bg-white hover:border-stone/80"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          buyerType === option.value
                            ? "text-red"
                            : "text-charcoal"
                        }`}
                      >
                        {option.label}
                      </p>
                      <p className="mt-0.5 text-xs text-warm-grey">
                        {option.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your stamp duty<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Based on the current SDLT rates for England &amp; Northern
                  Ireland.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              {/* Total — hero metric */}
              <CalculatorResultCard
                label="Total stamp duty"
                value={formatCurrency(result.totalDuty)}
                sub={`Effective rate: ${formatPercent(result.effectiveRate)}`}
                accent
                large
              />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Property price"
                  value={formatCurrency(price)}
                  sub={
                    BUYER_TYPE_OPTIONS.find((o) => o.value === buyerType)
                      ?.label
                  }
                />
                <CalculatorResultCard
                  label="Total cost"
                  value={formatCurrency(price + result.totalDuty)}
                  sub="Purchase price + stamp duty"
                />
              </div>

              {/* Band breakdown */}
              <BandBreakdown bands={result.bands} />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How stamp duty works<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Stamp Duty Land Tax is calculated on a tiered basis — you only
              pay the higher rates on the portion of the price that falls
              within each band, not on the full purchase price. It works the
              same way as income tax brackets.
            </p>
            <p>
              For example, if you buy a property for £350,000 as a home mover,
              the first £250,000 is tax-free, and you pay 5% on the remaining
              £100,000 — giving a total of £5,000 (an effective rate of 1.43%).
            </p>
            <p>
              First-time buyers benefit from a higher nil-rate threshold of
              £425,000, provided the property costs no more than £625,000.
              Additional properties (buy-to-let and second homes) attract
              a 5% surcharge on every band.
            </p>
          </div>

          <p className="mt-6 text-xs text-warm-grey">
            Rates shown are for England and Northern Ireland.
            Scotland (LBTT) and Wales (LTT) have different rates.
            Last updated: February 2026.{" "}
            <a
              href="https://www.gov.uk/stamp-duty-land-tax/residential-property-rates"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate"
            >
              Source: GOV.UK
            </a>
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            {
              stat: "73%",
              label: "of buyers watch property video before viewing",
            },
            {
              stat: "2x",
              label: "more enquiries on listings with video",
            },
            {
              stat: "£5,000",
              label: "typical stamp duty on a £350k property",
            },
            {
              stat: "<60s",
              label: "to create a listing video with QuiteRight",
            },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/mortgage-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Mortgage calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate your monthly repayments and total interest.
              </p>
            </Link>
            <Link
              href="/tools/instruction-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Instruction calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                See how much extra revenue video marketing could earn your
                agency.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Find an agent who markets with video
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            The best estate agents don&rsquo;t just list your property — they
            market it. Browse agents who use video to get more viewings and
            faster sales.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/directory"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Browse estate agents
            </Link>
            <Link
              href="/areas"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Explore area guides
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
